import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import isEqual from 'fast-deep-equal/react';

import { Divider, Stack } from '@mui/material';
import { Palette } from '@mui/icons-material';

import {
  AttachmentUploadList,
  Button,
  FormDateTimeInput,
  FormSwitchInput,
  FormTextInput,
  HStack,
  IModalPropsBase,
  IUploadedAttachment,
  LinkEditorList,
  Modal
} from '@dw/components/core';
import { useConfirm } from '@dw/hooks';
import { useCreateNoticeMutation, useUpdateNoticeMutation } from '@dw/hooks/api/NoticesApi';
import { useGetNoticeThemes } from '@dw/hooks/api/NoticeThemesApi';
import { BLOB_UPLOAD_TYPE } from '@dw/models/blob';
import {
  INotice,
  INoticeLink,
  NOTICE_MAX_ATTACHMENTS,
  NOTICE_MAX_ATTACHMENT_SIZE_MB,
  NOTICE_MAX_LINKS,
  NOTICE_TEXT_MAX_LENGTH,
  NOTICE_TITLE_MAX_LENGTH
} from '@dw/models/notice';
import { dayjs, Dayjs } from '@dw/dayjs';
import { formatFileSize, getSpacingCss } from '@dw/utils';

import { NoticeThemeManagerModal } from './NoticeThemeManagerModal';
import { NoticeThemePicker } from './NoticeThemePicker';

interface INoticeForm {
  title: string;
  text: string;
  showAt: Dayjs | null;
  removeAt: Dayjs | null;
  links: INoticeLink[];
  attachments: IUploadedAttachment[];
  visibleToTeachers: boolean;
  addToTop: boolean;
}

/**
 * The form for a notice, whether it is new or being edited.
 *
 * The chosen theme is held outside the form because the picker owns its own value and reports
 * changes rather than binding to a form field - threading it through form state would mean two
 * sources of truth for one choice.
 */
const initForm = (notice?: INotice): INoticeForm => ({
  title: notice?.title ?? '',
  text: notice?.text ?? '',
  showAt: notice?.showAt ?? null,
  removeAt: notice?.removeAt ?? null,
  // Seeded from the notice, NOT reset to []. The API's update is a full replace, so a studio who
  // edits only the text must send the existing links straight back or they are silently wiped.
  links: notice?.links ?? [],
  // Formatted here rather than left blank: the upload list shows this string, so an attachment
  // already on the notice would otherwise sit there with no size beside it while a freshly picked
  // one showed its own.
  attachments: (notice?.attachments ?? []).map(x => ({
    key: x.key,
    fileName: x.fileName,
    sizeInBytes: x.fileSizeInBytes,
    fileSizeFormatted: formatFileSize(x.fileSizeInBytes)
  })),
  visibleToTeachers: notice?.visibleToTeachers ?? false,
  addToTop: true
});

interface INoticeEditorModalProps extends IModalPropsBase {
  /** Absent for a new notice. Present, and the modal edits that notice in place. */
  notice?: INotice;
}

/**
 * Write a notice, or edit one already on the board.
 *
 * One modal rather than two: create and edit differ in exactly one visible field - where a NEW
 * notice lands on the board - and a second near-identical dialog is how the two drift apart.
 */
export const NoticeEditorModal: FunctionComponent<INoticeEditorModalProps> = (props: INoticeEditorModalProps) => {

  const { open, onClose, notice } = props;

  const { confirm } = useConfirm();

  const isEdit = !!notice;

  const [formData, setFormData] = useState<INoticeForm>(initForm(notice));
  const [noticeThemeId, setNoticeThemeId] = useState<string | undefined>(notice?.themeId);
  const [themesOpen, setThemesOpen] = useState(false);

  const { data: themes } = useGetNoticeThemes({ enabled: open });
  const themeList = useMemo(() => themes ?? [], [themes]);

  // Re-seeded on every open, and on the notice itself: the dialog stays mounted between openings,
  // so without this, editing one notice and then another would show the first one's text.
  useEffect(() => {
    setFormData(initForm(notice));
    setNoticeThemeId(notice?.themeId);
  }, [open, notice]);

  /*
    A NEW notice lands on the first theme in the school's palette. Done here rather than as a
    useState default because the palette arrives after the dialog opens - and only when nothing has
    been chosen yet, so it can never overwrite a studio's pick or an edited notice's own theme.
  */
  useEffect(() => {
    if (!noticeThemeId && themeList.length > 0) {
      setNoticeThemeId(themeList[0].noticeThemeId);
    }
  }, [themeList, noticeThemeId]);

  const { mutate: createNotice, isPending: creating } = useCreateNoticeMutation({ onSuccess: () => onClose() });
  const { mutate: updateNotice, isPending: updating } = useUpdateNoticeMutation({ onSuccess: () => onClose() });

  const busy = creating || updating;

  const handleSubmit = (fd: INoticeForm) => {
    // Trimmed to null rather than sent as an empty string: "no title" is a real state the apps
    // render differently, and "" would be a title that happens to be blank.
    const title = fd.title.trim() || undefined;
    const attachmentKeys = fd.attachments.map(x => x.key);

    // The pickers hand back school-local wall-clock times; the API stores and compares UTC.
    const showAt = fd.showAt ? dayjs(fd.showAt).toISOString() : undefined;
    const removeAt = fd.removeAt ? dayjs(fd.removeAt).toISOString() : undefined;

    // Never empty by the time the form can be submitted: the palette is never empty, and the effect
    // above seeds the first theme as soon as it arrives.
    const chosenThemeId = noticeThemeId ?? themeList[0]?.noticeThemeId ?? '';

    if (isEdit) {
      updateNotice({
        noticeId: notice.noticeId,
        title,
        text: fd.text,
        noticeThemeId: chosenThemeId,
        showAt,
        removeAt,
        attachmentKeys,
        links: fd.links,
        visibleToTeachers: fd.visibleToTeachers
      });
      return;
    }

    createNotice({
      title,
      text: fd.text,
      noticeThemeId: chosenThemeId,
      showAt,
      removeAt,
      attachmentKeys,
      links: fd.links,
      visibleToTeachers: fd.visibleToTeachers,
      addToTop: fd.addToTop
    });
  };

  const handleClose = () => {
    const isDirty = formData.title !== (notice?.title ?? '')
      || formData.text !== (notice?.text ?? '')
      // Compared against the notice's own theme when editing, and against the palette's first theme
      // when new - which is what the effect above seeds, so simply opening the dialog is not "dirty".
      || noticeThemeId !== (notice?.themeId ?? themeList[0]?.noticeThemeId)
      || formData.attachments.length !== (notice?.attachments?.length ?? 0)
      // Deep, not a length compare: fixing a typo in one link's address leaves the count untouched,
      // and a length check would let that edit be discarded on close without a prompt.
      || !isEqual(formData.links, notice?.links ?? []);

    if (!isDirty) {
      onClose();
      return;
    }

    confirm({
      title: isEdit ? 'Discard changes?' : 'Discard notice?',
      message: isEdit
        ? 'Your changes to this notice will be lost.'
        : 'This notice will not be pinned to the board.',
      positiveText: 'Discard',
      onPositive: () => onClose()
    });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      confirmBackdropClose
      maxWidth='sm'
      fullScreenOnMobile
      title={isEdit ? 'Edit notice' : 'New notice'}
      contentTopPadding={getSpacingCss(2)}
      formWrapper={{
        formData,
        formDataChanged: setFormData,
        disabled: busy,
        onSubmit: handleSubmit
      }}
      actions={(
        <>
          <Button variant='text' color='error' onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant='text' type='submit' loading={busy}>
            {isEdit ? 'Save notice' : 'Pin to board'}
          </Button>
        </>
      )}
    >
      <Stack rowGap={2}>

        <FormTextInput<INoticeForm>
          name='title'
          label='Title'
          maxLength={NOTICE_TITLE_MAX_LENGTH}
          margin='none'
          helperText='Optional — a short heading makes a busy board easier to scan'
        />

        <FormTextInput<INoticeForm>
          name='text'
          label='Notice'
          required
          multiline
          minRows={3}
          maxRows={10}
          maxLength={NOTICE_TEXT_MAX_LENGTH}
          margin='none'
        />

        <Divider />

        {/*
          The palette, plus the way into managing it. Putting "Manage themes" here rather than only
          on the page header is the point at which a studio actually discovers they can change these
          colours - it is the moment they are looking for one that is not there.
        */}
        <Stack rowGap={1}>
          <NoticeThemePicker
            label='Colour'
            themes={themeList}
            value={noticeThemeId}
            onChange={setNoticeThemeId}
            disabled={busy}
          />

          <HStack breakpoint={false} justifyContent='flex-end'>
            <Button
              type='button'
              size='small'
              variant='text'
              startIcon={<Palette fontSize='small' />}
              onClick={() => setThemesOpen(true)}
              disabled={busy}
            >
              Manage themes
            </Button>
          </HStack>
        </Stack>

        <Divider />

        <FormDateTimeInput<INoticeForm>
          name='showAt'
          label='Show from'
          margin='none'
          helperText='Leave empty to show it straight away'
        />

        <FormDateTimeInput<INoticeForm>
          name='removeAt'
          label='Remove on'
          margin='none'
          helperText='Leave empty to leave it up until you take it down'
        />

        <FormSwitchInput<INoticeForm>
          name='visibleToTeachers'
          label='Also show to teachers'
        />

        {/*
          Create only. By edit time the notice already has a position on the board, and the way to
          change a position is to drag it - offering it here would give two controls authority over
          one order.
        */}
        {!isEdit && (
          <FormSwitchInput<INoticeForm>
            name='addToTop'
            label='Add to the top of the board'
          />
        )}

        <Divider />

        <LinkEditorList
          value={formData.links}
          onChange={(links) => setFormData(fd => ({ ...fd, links }))}
          maxLinks={NOTICE_MAX_LINKS}
          disabled={busy}
        />

        <Divider />

        <AttachmentUploadList
          value={formData.attachments}
          onChange={(attachments) => setFormData(fd => ({ ...fd, attachments }))}
          uploadType={BLOB_UPLOAD_TYPE.NOTICE_ATTACHMENT}
          acceptedMimeTypes={['application/pdf', 'image/*']}
          sizeRule={{ maxFiles: NOTICE_MAX_ATTACHMENTS, maxSizePerFileMb: NOTICE_MAX_ATTACHMENT_SIZE_MB }}
          disabled={busy}
        />

        <Divider />
      </Stack>
    </Modal>

    {/*
      A SIBLING of the notice dialog, not a child of it. Both are portalled out of the DOM anyway,
      but nesting one form dialog inside another's `Form` reads as a nested <form> and is the sort
      of thing a later edit "tidies" into one.

      Opened over the notice being written rather than instead of it: a studio who realises
      mid-notice that they want a new colour should not have to discard the notice to make one. The
      palette invalidates on save, so a theme added here is in the picker behind it.
    */}
    <NoticeThemeManagerModal open={themesOpen} onClose={() => setThemesOpen(false)} />
    </>
  );
};
